import { PencilLine, Save, Trash, X } from "lucide-react";
import { Button } from "../utils/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import React from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../utils/components/ui/accordion";
import TableComponent from "../components/common/TableComponent";
import { useAddCustomer } from "../service/customer";
import { toast } from "react-toastify";
import useCustomerStore from "../store/useCustomerStore";
import { CustomerCombobox } from "../components/CustomerCombobox";
import { ProductDropdown } from "../components/ProductDropdown";
import { useEffect, useState } from "react";
import { useGetAllProductData } from "../service/productService";
import { useQueryManager } from "../hooks/useQueryManager";
import {
  useAddQuotation,
  useGetQuotationById,
  useQuotationPrint,
  useUpdateQuotation,
} from "../service/quatationService";
import { useNavigate } from "react-router-dom";
import QuoteSuccessModal from "../components/SuccessModal";
import { useQueryClient } from "@tanstack/react-query";
import { QuotationProduct } from "../types/quatationType";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../utils/components/ui/breadcrumb";
import {
  calculateBaseQuantity,
  calculateUnitPrice,
  getDefaultUnitOption,
  getUnitOption,
  normalizeUnitOptions,
} from "../utils/unitConversion";
import {
  findProductById,
  formatProductDropdownLabel,
} from "../utils/productDropdown";
const customerSchema = z.object({
  name: z.string().optional(),
  phoneNumber: z.string().min(1, "Phone Number is required"),
  email: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  trn: z.string().optional(),
  creditLimit: z.coerce.number().min(0).optional().default(0),
  creditPeriod: z.coerce.number().min(0).optional().default(0),
});

type CustomerFormInputs = z.infer<typeof customerSchema>;

// 1. Zod schema for product entry
const productSchema = z.object({
  productId: z.coerce.number().min(1, "Product is required"),
  quantity: z.coerce.number().positive("Quantity is required"),
  unitPrice: z.coerce.number().min(0, "Unit price is required"),
  discountAmount: z.coerce.number().min(0, "Discount is required"),
  isDiscountPercentage: z
    .union([z.boolean(), z.string()])
    .transform((val) => val === true || val === "true"),
  taxPercentage: z.coerce.number().min(0, "Tax is required"),
  unit: z.string(),
  conversionToBaseUnit: z.coerce.number().positive(),
  baseQuantity: z.coerce.number().min(0),
});
type ProductFormInputs = z.output<typeof productSchema>;

// Edit Product Modal Component
interface EditProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: ProductFormInputs) => void;
  productData: ProductFormInputs | null;
  products: any[];
  editIndex: number | null;
}

const EditProductModal: React.FC<EditProductModalProps> = ({
  isOpen,
  onClose,
  onSave,
  productData,
  products,
  editIndex,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<ProductFormInputs>({
    resolver: zodResolver(productSchema),
    defaultValues: productData || {
      productId: 0,
      quantity: 1,
      unitPrice: 0,
      discountAmount: 0,
      isDiscountPercentage: true,
      taxPercentage: 5,
      unit: "",
      conversionToBaseUnit: 1,
      baseQuantity: 0,
    },
  });

  const watchedProductId = watch("productId");

  React.useEffect(() => {
    if (productData) {
      reset(productData);
    }
  }, [productData, reset]);

  React.useEffect(() => {
    const foundProduct = products?.find(
      (p: any) => Number(p.id) === Number(watchedProductId),
    );
    if (foundProduct) {
      setValue(
        "unitPrice",
        calculateUnitPrice(
          foundProduct.price,
          productData?.conversionToBaseUnit || 1,
        ),
      );
      // Only update unit and conversion if they are not already set correctly for this product
      // This prevents overriding the user's selected unit when opening the modal for editing
      if (!productData || productData.productId !== watchedProductId) {
        const option = getDefaultUnitOption(
          foundProduct.unitOptions,
          foundProduct.unit,
        );
        setValue("unit", option?.unit || foundProduct.unit || "");
        setValue("conversionToBaseUnit", option?.conversionToBaseUnit || 1);
      }
    }
  }, [watchedProductId, productData, products, setValue]);

  const onSubmit = (data: ProductFormInputs) => {
    onSave(data);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-[#21314A]">
            Edit Product {editIndex !== null ? `#${editIndex + 1}` : ""}
          </h2>
          <button
            onClick={onClose}
            className="text-[#52627C] hover:text-[#21314A] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Product Selection */}
            <div>
              <label
                htmlFor="productId"
                className="text-sm font-medium text-[#52627C] mb-1.5 block"
              >
                Product *
              </label>
              <ProductDropdown
                useApiFilter={true}
                className="w-full h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4"
                products={products}
                onChange={(val) => {
                  setValue("productId", Number(val), { shouldValidate: true });
                }}
                value={String(watchedProductId || "")}
              />
              {errors.productId && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.productId.message}
                </p>
              )}
            </div>

            {/* Unit */}
            <div>
              <label
                htmlFor="unit"
                className="text-sm font-medium text-[#52627C] mb-1.5 block"
              >
                Unit
              </label>
              <select
                id="unit"
                className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                {...register("unit")}
                onChange={(event) => {
                  const foundProduct = products?.find(
                    (p: any) => Number(p.id) === Number(watchedProductId),
                  );
                  const option = getUnitOption(
                    foundProduct?.unitOptions,
                    event.target.value,
                    foundProduct?.unit,
                  );
                  setValue("unit", event.target.value);
                  setValue(
                    "conversionToBaseUnit",
                    option?.conversionToBaseUnit || 1,
                  );
                  setValue(
                    "unitPrice",
                    calculateUnitPrice(
                      foundProduct?.price || 0,
                      option?.conversionToBaseUnit || 1,
                    ),
                  );
                }}
              >
                {normalizeUnitOptions(
                  products?.find(
                    (p: any) => Number(p.id) === Number(watchedProductId),
                  )?.unitOptions,
                  products?.find(
                    (p: any) => Number(p.id) === Number(watchedProductId),
                  )?.unit,
                ).map((option) => (
                  <option key={option.unit} value={option.unit}>
                    {option.unit}
                  </option>
                ))}
              </select>
            </div>

            {/* Quantity */}
            <div>
              <label
                htmlFor="quantity"
                className="text-sm font-medium text-[#52627C] mb-1.5 block"
              >
                Quantity *
              </label>
              <input
                id="quantity"
                type="number"
                min="0.0001"
                step="any"
                placeholder="Quantity"
                className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                {...register("quantity")}
              />
              {errors.quantity && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.quantity.message}
                </p>
              )}
            </div>

            {/* Unit */}
            <div>
              <label
                htmlFor="unit"
                className="text-sm font-medium text-[#52627C] mb-1.5 block"
              >
                Unit
              </label>
              <select
                id="unit"
                className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                {...register("unit")}
                onChange={(event) => {
                  const product = findProductById(products, watchedProductId);
                  const option = getUnitOption(
                    product?.unitOptions,
                    event.target.value,
                    product?.unit,
                  );
                  setValue("unit", event.target.value, { shouldValidate: true });
                  setValue(
                    "conversionToBaseUnit",
                    option?.conversionToBaseUnit || 1,
                  );
                  setValue(
                    "unitPrice",
                    calculateUnitPrice(
                      product?.price || 0,
                      option?.conversionToBaseUnit || 1,
                    ),
                  );
                }}
              >
                {normalizeUnitOptions(
                  findProductById(products, watchedProductId)?.unitOptions,
                  findProductById(products, watchedProductId)?.unit,
                ).map((option) => (
                  <option key={option.unit} value={option.unit}>
                    {option.unit}
                  </option>
                ))}
              </select>
            </div>

            {/* Unit Price */}
            <div>
              <label
                htmlFor="unitPrice"
                className="text-sm font-medium text-[#52627C] mb-1.5 block"
              >
                Unit Price
              </label>
              <input
                id="unitPrice"
                type="number"
                min="0"
                step="any"
                placeholder="Unit Price"
                className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                {...register("unitPrice")}
              />
              {errors.unitPrice && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.unitPrice.message}
                </p>
              )}
            </div>

            {/* Discount Amount */}
            <div>
              <label
                htmlFor="discountAmount"
                className="text-sm font-medium text-[#52627C] mb-1.5 block"
              >
                Discount
              </label>
              <input
                id="discountAmount"
                type="number"
                min="0"
                step="any"
                placeholder="Discount"
                className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                {...register("discountAmount")}
              />
              {errors.discountAmount && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.discountAmount.message}
                </p>
              )}
            </div>

            {/* Discount Type */}
            <div>
              <label
                htmlFor="isDiscountPercentage"
                className="text-sm font-medium text-[#52627C] mb-1.5 block"
              >
                Discount Type
              </label>
              <select
                id="isDiscountPercentage"
                className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                {...register("isDiscountPercentage")}
              >
                <option value="true">Percentage (%)</option>
                <option value="false">Fixed Amount</option>
              </select>
            </div>

            {/* Tax Percentage */}
            <div>
              <label
                htmlFor="taxPercentage"
                className="text-sm font-medium text-[#52627C] mb-1.5 block"
              >
                Tax (%)
              </label>
              <input
                id="taxPercentage"
                type="number"
                min="0"
                step="any"
                placeholder="Tax Percentage"
                className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none cursor-not-allowed opacity-60"
                disabled
                {...register("taxPercentage")}
              />
              {errors.taxPercentage && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.taxPercentage.message}
                </p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-[#EEF2FA]">
            <button
              type="button"
              onClick={onClose}
              className="h-11 rounded-[12px] border border-[#D7E2F5] px-6 text-[#52627C] hover:bg-[#F5F8FD] font-medium text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE] font-medium text-sm transition-colors"
            >
              Update Product
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreateDraft = () => {
  const { allParams, setValue, getValues } = useQueryManager();

  const { data: draftData } = useGetQuotationById(getValues("id")[0]);

  const { setCurrentCustomer, currentCustomer, clearCurrentCustomer } =
    useCustomerStore();

  const [productFilter, setProductFilter] = useState("");
  const branchId = Number(localStorage.getItem("branchId")) || undefined;
  const { data: productData, isLoading: productsLoading } =
    useGetAllProductData({
    pageNo: 0,
    pageSize: 1000,
    status: true,
    keyword: productFilter,
    branchId,
  });
  const ApiProducts = productData?.productResponseList || [];

  // Modal state for editing
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingProduct, setEditingProduct] =
    useState<ProductFormInputs | null>(null);

  const handleDeleteProduct = (index: number) => {
    setProductArray((prev) => prev.filter((_, i) => i !== index));
    toast.success("Product deleted successfully!");
  };

  // Handle edit product
  const handleEditProduct = (index: number) => {
    setEditingIndex(index);
    setEditingProduct(productArray[index]);
    setIsEditModalOpen(true);
  };

  // Handle save edited product
  const handleSaveEditedProduct = (updatedProduct: ProductFormInputs) => {
    if (editingIndex !== null) {
      const selectedProd = ApiProducts.find((p: any) => p.id === updatedProduct.productId);
      if (selectedProd) {
        const costPrice = selectedProd.latestPurchasePrice ?? selectedProd.buyingPrice ?? 0;
        const costPriceForUnit = costPrice * (updatedProduct.conversionToBaseUnit || 1);
        if (costPriceForUnit > 0 && updatedProduct.unitPrice < costPriceForUnit) {
          toast.warning("Warning: Product is selling under purchased cost!");
        }
      }

      setProductArray((prev) =>
        prev.map((item, index) =>
          index === editingIndex
            ? {
                ...updatedProduct,
                baseQuantity: calculateBaseQuantity(
                  updatedProduct.quantity,
                  updatedProduct.conversionToBaseUnit,
                ),
              }
            : item,
        ),
      );
      toast.success("Product updated successfully!");
    }
    setIsEditModalOpen(false);
    setEditingIndex(null);
    setEditingProduct(null);
  };

  // Close modal handler
  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setEditingIndex(null);
    setEditingProduct(null);
  };

  const brandsTColumns = [
    { header: "Sl.", accessor: "sl" },
    { header: "Product", accessor: "product" },
    { header: "In Arabic", accessor: "inArabic" },
    { header: "Quantity", accessor: "quantity" },
    { header: "Unit", accessor: "unit" },
    { header: "Tax", accessor: "tax" },
    { header: "Unit Price", accessor: "unitPrice" },
    { header: "Discount", accessor: "discount" },
    { header: "Total", accessor: "total" },
    {
      header: "Action",
      accessor: "action",
      className: "text-center",
      cell: ({ row }: any) => (
        <div className="flex gap-x-4 justify-center">
          <button
            className="text-gray-500 hover:text-[#1A5EF9] cursor-pointer transition-colors"
            onClick={() => handleEditProduct(row.original.sl - 1)}
            title="Edit Product"
            aria-label="Edit Product"
          >
            <PencilLine className="w-5 h-5" />
            <span className="sr-only">Edit</span>
          </button>

          <button
            className="text-gray-500 hover:text-red-500 cursor-pointer transition-colors"
            onClick={() => handleDeleteProduct(row.original.sl - 1)}
            title="Delete Product"
            aria-label="Delete Product"
          >
            <Trash className="w-5 h-5" />
            <span className="sr-only">Delete</span>
          </button>
        </div>
      ),
    },
  ];

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues: getCustomerValues,
  } = useForm<CustomerFormInputs>({
    resolver: zodResolver(customerSchema),
    defaultValues: currentCustomer
        ? {
          name: currentCustomer.name || currentCustomer.username || "",
          phoneNumber: currentCustomer.phoneNumber || "",
          email: currentCustomer.email || "",
          address: currentCustomer.address || "",
          trn: currentCustomer.trn || "", // <-- Add this line
          creditLimit: Number(currentCustomer.creditLimit) || 0,
          creditPeriod: Number(currentCustomer.creditPeriod) || 0,
        }
      : {
          trn: "", // <-- Add this line for empty default
          creditLimit: 0,
          creditPeriod: 0,
        },
  });

  const [selectedProduct, setSelectedProduct] = useState<string>("");

  const navigate = useNavigate();

  const addQuotation = useAddQuotation({
    onSuccess: () => {
      toast.success("Quotation added successfully!");
      // Optionally reset productArray or navigate
      setProductArray([]);
      // navigate("/sales")
      setIsSuccessModalOpen(true);
      clearCurrentCustomer();

      // You can also add navigation or other logic here
    },
    onError: (error: Error) => {
      toast.error(`Failed to add quotation due to ${error.message}.`);
    },
  });

  const addCustomer = useAddCustomer({
    onSuccess: (data: any) => {
      console.log("onSuccess called", data);
      setCurrentCustomer(data);
      toast.success(`Customer ${data?.username} added successfully!`);
      reset();
      queryClient.invalidateQueries({ queryKey: ["useGetAllCustomers"] });
      queryClient.invalidateQueries({ queryKey: ["useInfiniteCustomerOptions"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to add customer: ${error.message}`);
    },
  });
  useEffect(() => {
    // Reset form fields when currentCustomer changes
    if (currentCustomer) {
      reset({
        name: currentCustomer.name || currentCustomer.username || "",
        phoneNumber: currentCustomer.phoneNumber || "",
        email: currentCustomer.email || "",
        address: currentCustomer.address || "",
        trn: currentCustomer.trn || "", // <-- Add this line
        creditLimit: Number(currentCustomer.creditLimit) || 0,
        creditPeriod: Number(currentCustomer.creditPeriod) || 0,
      });
    }
  }, [currentCustomer, reset]);

  // 2. State for product array
  const [productArray, setProductArray] = useState<ProductFormInputs[]>([]);

  console.log("productArray", productArray);

  // 3. React Hook Form for product
  const {
    register: registerProduct,
    handleSubmit: handleProductSubmit,
    reset: resetProduct,
    setValue: setProductValue,
    formState: { errors: productErrors },
  } = useForm<ProductFormInputs>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      productId: 0,
      quantity: 1,
      unitPrice: 0,
      discountAmount: 0,
      isDiscountPercentage: true,
      taxPercentage: 5,
      unit: "",
      conversionToBaseUnit: 1,
      baseQuantity: 0,
    },
  });

  useEffect(() => {
    const foundProduct = findProductById(ApiProducts, selectedProduct);
    if (foundProduct) {
      const option = getDefaultUnitOption(
        foundProduct.unitOptions,
        foundProduct.unit,
      );
      setProductValue(
        "unitPrice",
        calculateUnitPrice(
          foundProduct.price,
          option?.conversionToBaseUnit || 1,
        ),
      );
      setProductValue("unit", option?.unit || foundProduct.unit || "");
      setProductValue(
        "conversionToBaseUnit",
        option?.conversionToBaseUnit || 1,
      );
    }
  }, [selectedProduct, ApiProducts]);

  // 4. Add product handler
  const onAddProduct = (data: ProductFormInputs) => {
    console.log("onAddProduct called with", data);

    // 🔹 Check if the product already exists
    const alreadyExists = productArray.some(
      (item) => item.productId === data.productId,
    );

    if (alreadyExists) {
      toast.error("This product is already added!");
      return; // stop execution
    }

    const selectedProd = ApiProducts.find((p: any) => p.id === data.productId);
    if (selectedProd) {
      const costPrice = selectedProd.latestPurchasePrice ?? selectedProd.buyingPrice ?? 0;
      const costPriceForUnit = costPrice * (data.conversionToBaseUnit || 1);
      if (costPriceForUnit > 0 && data.unitPrice < costPriceForUnit) {
        toast.warning("Warning: Product is selling under purchased cost!");
      }
    }

    const item = {
      ...data,
      baseQuantity: calculateBaseQuantity(
        data.quantity,
        data.conversionToBaseUnit,
      ),
    };
    setProductArray((prev) => [...prev, item]);
    setValue("productArray", JSON.stringify([...productArray, item]));
    resetProduct();
    setSelectedProduct("");
    toast.success("Product added successfully!");
    reset();
  };

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [reqNumber, setReqNumber] = useState("");

  const handleCloseSuccessModal = () => {
    setIsSuccessModalOpen(false);
    navigate("/sales");
  };

  const printInvoice = useQuotationPrint({
    onSuccess: (data: string) => {
      // toast.success('Invoice printed successfully');
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(data);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
      } else {
        toast.error("Popup blocked! Please allow popups for this site.");
      }
      queryClient.invalidateQueries({ queryKey: ["useGetAllInvoice"] });
    },
    onError: (error: Error) => {
      toast.error(`Error printing invoice: ${error.message}`);
    },
  });
  const handlePrintQuote = () => {
    if (quoteId) {
      const pdfData = printInvoice.mutate({ invoiceId: quoteId });
    }
    // Implement print logic here
    // For now, just close the modal
    handleCloseSuccessModal();
  };
  const updateQuotation = useUpdateQuotation({
    onSuccess: (data: any) => {
      toast.success(`Quotation updated successfully!`);
      navigate("/sales/draft");
    },
    onError: (error: Error) => {
      toast.error(`Error updating quotation: ${error.message}`);
    },
  });
  const onSaveQuotation = () => {
    const trn = getCustomerValues("trn") || "";
    if (allParams.id) {
      const postData = {
        customerId: (currentCustomer?.id as string) || "",
        quotationProductRequests: productArray,
        isDraft: true,
        quotationNumber: draftData?.quotationNumber,
        reqNumber,
        trn,
      };
      updateQuotation.mutate({
        ...postData,
        id: allParams.id,
      });
    } else {
      const postData = {
        customerId: (currentCustomer?.id as string) || "",
        quotationProductRequests: productArray,
        isDraft: true,
        reqNumber,
        trn,
      };

      addQuotation.mutate(postData, {
        onSuccess: (data: any) => {
          console.log(data, "id creat");
          setQuoteId(data?.id || data || null);
        },
      });
    }
  };

  const queryClient = useQueryClient();

  useEffect(() => {
    if (draftData && draftData.customer) {
      reset({
        name: draftData.customer.username || "",
        phoneNumber: draftData.customer.phoneNumber || "",
        email: draftData.customer.email || "",
        address: draftData.customer.address || "",
        trn: draftData.customer.trn || "", // <-- Add this line
        creditLimit: Number(draftData.customer.creditLimit) || 0,
        creditPeriod: Number(draftData.customer.creditPeriod) || 0,
      });
      setCurrentCustomer({
        id: draftData.customer.id + "",
        name: draftData.customer.username || "",
        email: draftData.customer.email || "",
        phoneNumber: draftData.customer.phoneNumber || "",
        address: draftData.customer.address || "",
        trn: draftData.customer.trn || "",
        creditLimit: draftData.customer.creditLimit,
        creditPeriod: draftData.customer.creditPeriod,
      });
    }
    setReqNumber(draftData?.reqNumber || "");
    // If you want to set product array as well:
    if (draftData && draftData.quotationProducts) {
      setProductArray(
        draftData.quotationProducts?.map((data: QuotationProduct) => ({
          productId: data.product?.id ?? 0,
          quantity: data.qty ?? 1,
          unitPrice: data.unitPrice ?? 0,
          discountAmount: data.discountAmount ?? 0,
          isDiscountPercentage: data.isDiscountPercentage ?? true,
          taxPercentage: data.rateOfTax ?? 0,
          unit: data.unit || data.product?.unit || "",
          conversionToBaseUnit: data.conversionToBaseUnit || 1,
          baseQuantity: data.baseQuantity || data.qty || 0,
        })) || [],
      );
    }
  }, [draftData, reset, setCurrentCustomer]);
  return (
    <>
      <div className="space-y-6 p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/sales">Draft</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Create Draft</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <h1 className="text-2xl font-bold text-[#1A5EF9]">Create Draft</h1>
        {/* Customer Details Card */}
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-[#21314A]">
              Customer Details
            </h2>
            {typeof currentCustomer === "string" && (
              <button
                className="h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE] font-medium text-sm flex items-center gap-2 transition-colors"
                onClick={handleSubmit((data) => {
                  let submitData = { ...data };
                  if (typeof currentCustomer === "string") {
                    submitData.name = currentCustomer || "";
                  }
                  addCustomer.mutate(submitData);
                })}
              >
                <Save className="w-4 h-4" />
                Save
              </button>
            )}
          </div>
          <form
            onSubmit={handleSubmit((data) => {
              let submitData = { ...data };
              if (typeof currentCustomer === "string") {
                submitData.name = currentCustomer;
              }
              addCustomer.mutate(submitData);
            })}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Customer
                </label>
                <CustomerCombobox
                  className="w-full h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4"
                  customer={currentCustomer}
                  onSelectCustomer={setCurrentCustomer}
                  onChange={setCurrentCustomer}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.name.message}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Phone
                </label>
                <input
                  {...register("phoneNumber")}
                  type="text"
                  placeholder="Phone Number"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
                {errors.phoneNumber && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.phoneNumber.message}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Email
                </label>
                <input
                  {...register("email")}
                  type="text"
                  placeholder="Email"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Address
                </label>
                <input
                  {...register("address")}
                  type="text"
                  placeholder="Address"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
                {errors.address && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.address.message}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  TRN Number
                </label>
                <input
                  {...register("trn")}
                  type="text"
                  placeholder="TRN Number"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
                {errors.trn && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.trn.message}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Credit Limit
                </label>
                <input
                  {...register("creditLimit")}
                  type="number"
                  step="any"
                  placeholder="Credit Limit"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Credit Period (days)
                </label>
                <input
                  {...register("creditPeriod")}
                  type="number"
                  step="1"
                  placeholder="Credit Period"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
            </div>
          </form>
        </div>
        {/* Add Product Card */}
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6">
          <h2 className="text-lg font-semibold text-[#21314A] mb-5">
            Add Product
          </h2>
          <form onSubmit={handleProductSubmit(onAddProduct)}>
            <div className="grid grid-cols-8 gap-3 items-end">
              <div className="col-span-2">
                <label
                  htmlFor="productId"
                  className="text-sm font-medium text-[#52627C] mb-1.5 block"
                >
                  Product
                </label>
                <ProductDropdown
                  useApiFilter={true}
                  apiFilterFunction={(input) => {
                    setProductFilter(input);
                  }}
                  className="w-full h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4"
                  products={ApiProducts}
                  loading={productsLoading}
                  onChange={(val) => {
                    setSelectedProduct(val);
                    setProductValue("productId", Number(val), {
                      shouldValidate: true,
                    });
                  }}
                  value={selectedProduct}
                />
                {productErrors.productId && (
                  <p className="text-red-500 text-xs mt-1">
                    {productErrors.productId.message}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Unit
                </label>
                <select
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-2 text-sm"
                  {...registerProduct("unit")}
                  onChange={(event) => {
                    const product = findProductById(
                      ApiProducts,
                      selectedProduct,
                    );
                    const option = getUnitOption(
                      product?.unitOptions,
                      event.target.value,
                      product?.unit,
                    );
                    setProductValue("unit", event.target.value);
                    setProductValue(
                      "conversionToBaseUnit",
                      option?.conversionToBaseUnit || 1,
                    );
                    setProductValue(
                      "unitPrice",
                      calculateUnitPrice(
                        product?.price || 0,
                        option?.conversionToBaseUnit || 1,
                      ),
                    );
                  }}
                >
                  {normalizeUnitOptions(
                    findProductById(ApiProducts, selectedProduct)?.unitOptions,
                    findProductById(ApiProducts, selectedProduct)?.unit,
                  ).map((option) => (
                    <option key={option.unit} value={option.unit}>
                      {option.unit}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-1">
                <label
                  htmlFor="quantity"
                  className="text-sm font-medium text-[#52627C] mb-1.5 block"
                >
                  Quantity
                </label>
                <input
                  id="quantity"
                  type="number"
                  placeholder="Qty"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                  {...registerProduct("quantity")}
                />
                {productErrors.quantity && (
                  <p className="text-red-500 text-xs mt-1">
                    {productErrors.quantity.message}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label
                  htmlFor="unitPrice"
                  className="text-sm font-medium text-[#52627C] mb-1.5 block"
                >
                  Unit Price
                </label>
                <input
                  id="unitPrice"
                  type="number"
                  step="any"
                  placeholder="Unit Price"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                  {...registerProduct("unitPrice")}
                />
                {productErrors.unitPrice && (
                  <p className="text-red-500 text-xs mt-1">
                    {productErrors.unitPrice.message}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label
                  htmlFor="discountAmount"
                  className="text-sm font-medium text-[#52627C] mb-1.5 block"
                >
                  Discount
                </label>
                <div className="flex gap-1">
                  <input
                    id="discountAmount"
                    type="number"
                    step="any"
                    placeholder="0"
                    className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-3 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                    {...registerProduct("discountAmount")}
                  />
                  <select
                    id="isDiscountPercentage"
                    className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-2 text-sm text-[#23324D] outline-none"
                    {...registerProduct("isDiscountPercentage")}
                  >
                    <option value="true">%</option>
                    <option value="false">Amt</option>
                  </select>
                </div>
                {productErrors.discountAmount && (
                  <p className="text-red-500 text-xs mt-1">
                    {productErrors.discountAmount.message}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label
                  htmlFor="taxPercentage"
                  className="text-sm font-medium text-[#52627C] mb-1.5 block"
                >
                  Tax (%)
                </label>
                <input
                  disabled
                  id="taxPercentage"
                  type="number"
                  placeholder="Tax"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none cursor-not-allowed opacity-60"
                  {...registerProduct("taxPercentage")}
                />
                {productErrors.taxPercentage && (
                  <p className="text-red-500 text-xs mt-1">
                    {productErrors.taxPercentage.message}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                className="h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE] font-medium text-sm transition-colors"
              >
                Add Product
              </button>
            </div>
          </form>
        </div>
        {/* Products Table Card */}
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6">
          <h2 className="text-lg font-semibold text-[#21314A] mb-5">
            Products
          </h2>
          {productArray.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-[#52627C] text-sm">
              No products added yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <TableComponent
                columns={brandsTColumns}
                data={productArray.map((item, index) => ({
                  sl: (index + 1).toString(),
                  product:
                    findProductById(ApiProducts, item.productId)
                      ?.productName || "",
                  inArabic:
                    findProductById(ApiProducts, item.productId)
                      ?.productName || "",
                  quantity: item.quantity.toString(),
                  unit: item.unit || "",
                  tax: item.taxPercentage.toString() + "%",
                  unitPrice: item.unitPrice.toString(),
                  discount:
                    item.discountAmount.toString() +
                    (item.isDiscountPercentage ? "%" : ""),
                  total: (
                    (item.unitPrice * item.quantity -
                      (item.isDiscountPercentage
                        ? (item.unitPrice *
                            item.quantity *
                            item.discountAmount) /
                          100
                        : item.discountAmount)) *
                    (1 + item.taxPercentage / 100)
                  ).toFixed(3),
                  action: "",
                }))}
              />
            </div>
          )}
        </div>

        {/* Edit Product Modal */}
        <EditProductModal
          isOpen={isEditModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveEditedProduct}
          productData={editingProduct}
          products={ApiProducts || []}
          editIndex={editingIndex}
        />

        {/* Totals Card */}
        <div className="flex justify-end">
          <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6 w-full max-w-sm">
            <h2 className="text-lg font-semibold text-[#21314A] mb-4">
              Summary
            </h2>
            <div className="space-y-0">
              <div className="pb-4">
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Req Number
                </label>
                <input
                  type="text"
                  value={reqNumber}
                  onChange={(e) => setReqNumber(e.target.value)}
                  placeholder="Enter req number"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-dotted border-[#E8EEF9]">
                <span className="text-sm text-[#52627C]">Net Total</span>
                <span className="text-sm font-medium text-[#21314A]">
                  {productArray
                    .reduce(
                      (sum, item) => sum + item.unitPrice * item.quantity,
                      0,
                    )
                    .toFixed(3)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-dotted border-[#E8EEF9]">
                <span className="text-sm text-[#52627C]">Tax</span>
                <span className="text-sm font-medium text-[#21314A]">
                  {productArray
                    .reduce((sum, item) => {
                      const base =
                        item.unitPrice * item.quantity -
                        (item.isDiscountPercentage
                          ? (item.unitPrice *
                              item.quantity *
                              item.discountAmount) /
                            100
                          : item.discountAmount);
                      return sum + (base * item.taxPercentage) / 100;
                    }, 0)
                    .toFixed(3)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-dotted border-[#E8EEF9]">
                <span className="text-sm text-[#52627C]">Discount</span>
                <span className="text-sm font-medium text-[#21314A]">
                  {productArray
                    .reduce((sum, item) => {
                      if (item.isDiscountPercentage) {
                        return (
                          sum +
                          (item.unitPrice *
                            item.quantity *
                            item.discountAmount) /
                            100
                        );
                      } else {
                        return sum + item.discountAmount;
                      }
                    }, 0)
                    .toFixed(3)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-dotted border-[#E8EEF9]">
                <span className="text-sm text-[#52627C]">Bill Discount</span>
                <span className="text-sm font-medium text-[#21314A]">0.00</span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-dotted border-[#E8EEF9]">
                <span className="text-sm text-[#52627C]">Rounding</span>
                <span className="text-sm font-medium text-[#21314A]">0.00</span>
              </div>
              <div className="flex justify-between items-center pt-3">
                <span className="text-base font-bold text-[#1A5EF9]">
                  Total
                </span>
                <span className="text-base font-bold text-[#1A5EF9]">
                  {productArray
                    .reduce((sum, item) => {
                      const base =
                        item.unitPrice * item.quantity -
                        (item.isDiscountPercentage
                          ? (item.unitPrice *
                              item.quantity *
                              item.discountAmount) /
                            100
                          : item.discountAmount);
                      return sum + base * (1 + item.taxPercentage / 100);
                    }, 0)
                    .toFixed(3)}
                </span>
              </div>
            </div>
            <button
              onClick={onSaveQuotation}
              className="mt-5 h-11 w-full rounded-[12px] bg-[#1A5EF9] text-white hover:bg-[#0E4FDE] font-medium text-sm transition-colors"
            >
              Save Draft
            </button>
          </div>
        </div>
      </div>
      <QuoteSuccessModal
        isOpen={isSuccessModalOpen}
        onClose={handleCloseSuccessModal}
        onPrintQuote={handlePrintQuote}
        quoteId={quoteId ?? ""}
      />
    </>
  );
};

export default CreateDraft;
